import React from 'react';
import { areEqual } from 'react-window';
import DiamondFilled from '@uber/base-icons/diamond-filled';
import { styled } from 'baseui';
import { useAtomValue } from 'jotai';
import { annotationIdToLabelMapAtom, annotationMetadataAtom } from '../../../../../labelling-v2/atoms/annotation-atoms';
import { useIsAnnotationSelected } from '../../../../../labelling-v2/atoms/tool-atoms';
import { getKeyframeEntry, hasNextFrameInSpan, hasPreviousFrameInSpan, isFrameInAnySpan } from '../../../../../labelling-v2/utils/frame-span-utils';
import { resolveAnnotationType } from '../../../../../labelling-v2/utils/resolve-annotation-type';
import type { AnnotationInfoT, OnAnnotationCellClick } from './types';
const Container = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  borderBottom: `1px solid ${$theme.colors.borderOpaque}`,
  cursor: 'pointer'
}));
const Connector = styled<'div', {
  $isVisible: boolean;
  $color: string;
  $isSelected: boolean;
}>('div', ({
  $theme,
  $isVisible,
  $color,
  $isSelected
}) => {
  let opacity = 0.5;
  if (!$isVisible) {
    opacity = 0;
  } else if ($isSelected) {
    opacity = 1;
  }
  return {
    flex: 1,
    height: $theme.sizing.scale0,
    background: $color,
    opacity,
    transition: 'opacity 300ms ease-out'
  };
});
const keyframeStyles = (isSelected: boolean) => ({
  transition: 'all 300ms ease-out',
  transform: isSelected ? 'scale(1.2)' : 'scale(1)'
});
const AutomatedKeyFrameIcon = styled<'div', {
  $isSelected: boolean;
}>('div', ({
  $theme,
  $isSelected
}) => ({
  height: $theme.sizing.scale500,
  width: $theme.sizing.scale500,
  borderRadius: '50%',
  background: $theme.colors.contentStateDisabled,
  ...keyframeStyles($isSelected)
}));
const DiamondIconWrapper = styled<'div', {
  $isSelected: boolean;
}>('div', ({
  $isSelected
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...keyframeStyles($isSelected)
}));
type CellDataT = {
  annotationObjectIds: string[];
  onCellClick: OnAnnotationCellClick;
};
type PropsT = {
  rowIndex: number;
  columnIndex: number;
  style: React.CSSProperties;
  data: CellDataT;
};
type KeyFramePropsT = {
  strokeColor: string;
  isAutomatedKeyframe: boolean;
  isSelected: boolean;
};
function KeyFrame({
  strokeColor,
  isAutomatedKeyframe,
  isSelected
}: KeyFramePropsT) {
  if (isAutomatedKeyframe) {
    return <AutomatedKeyFrameIcon $isSelected={isSelected} />;
  }
  return <DiamondIconWrapper $isSelected={isSelected}>
      <DiamondFilled color={strokeColor} width={14} height={14} />
    </DiamondIconWrapper>;
}
function AnnotationCellInternal({
  rowIndex,
  columnIndex,
  style,
  data
}: PropsT) {
  const {
    annotationObjectIds
  } = data;
  const annotationId = annotationObjectIds[rowIndex];
  const annotationMetadata = useAtomValue(annotationMetadataAtom);
  const annotationIdToLabelMap = useAtomValue(annotationIdToLabelMapAtom);
  const meta = annotationId ? annotationMetadata.get(annotationId) : undefined;
  const label = annotationId ? annotationIdToLabelMap.get(annotationId) : undefined;
  const isSelected = useIsAnnotationSelected(annotationId ?? '');
  const commonProps = {
    style,
    onClick: (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      let annotationData: Parameters<OnAnnotationCellClick>[3] = undefined;
      if (meta && label && annotationId) {
        const annotationInfo: AnnotationInfoT = {
          annotationType: resolveAnnotationType(label, meta.annotationType),
          annotationLabel: label
        };
        annotationData = {
          annotationId,
          annotationInfo
        };
      }
      data.onCellClick(e, rowIndex, columnIndex, annotationData);
    }
  };
  const isInSpan = meta ? isFrameInAnySpan(columnIndex, meta.spans) : false;
  if (!isInSpan || !meta || !label) {
    return <Container {...commonProps} />;
  }
  const isFirstFrame = !hasPreviousFrameInSpan(columnIndex, meta.spans);
  const isLastFrame = !hasNextFrameInSpan(columnIndex, meta.spans);
  const strokeColor = label.style.strokeColor;
  const keyframeEntry = getKeyframeEntry(columnIndex, meta.keyframes);
  const isKeyFrame = !!keyframeEntry || isFirstFrame;
  const isAutomatedKeyframe = keyframeEntry?.type === 'AUTOMATED';
  let ariaLabel = `Annotation for frame ${columnIndex + 1} and annotation ${annotationId}`;
  if (isKeyFrame) {
    ariaLabel += ' (Key frame)';
  }
  return <Container {...commonProps} aria-label={ariaLabel}>
      <Connector $isVisible={!isFirstFrame} $color={strokeColor} $isSelected={isSelected} />

      {isKeyFrame && <KeyFrame strokeColor={strokeColor} isAutomatedKeyframe={isAutomatedKeyframe} isSelected={isSelected} />}

      <Connector $isVisible={!isLastFrame} $color={strokeColor} $isSelected={isSelected} />
    </Container>;
}
export const AnnotationCell = React.memo(AnnotationCellInternal, areEqual);